import { Carousel } from "@mantine/carousel";
import {
  Grid,
  Image,
  Text,
  Button,
  Badge,
  Stack,
  Title,
  Divider,
  createStyles,
  rem,
} from "@mantine/core";
import { NavLink, Navigate } from "react-router-dom";
import { PageMeta, ProductJsonLd, BreadcrumbJsonLd } from "../seo";
import { pageTopSpacing } from "../../constant/Responsive";
import PageContainer from "../layout/PageContainer";
import ProductSpecs from "./ProductSpecs";
import ProductPricing from "./ProductPricing";
import { getCategoryLabel } from "../../constant/productCategories";

const useStyles = createStyles((theme) => ({
  imageWrapper: {
    aspectRatio: "1 / 1",
    overflow: "hidden",
    lineHeight: 0,
    width: "100%",
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[2]}`,
    "& .mantine-Carousel-root": { height: "100%" },
    "& .mantine-Carousel-viewport": { height: "100%" },
    "& .mantine-Carousel-slide": { height: "100%" },
    "& .mantine-Image-image": { objectFit: "contain" },
  },
  caption: {
    marginTop: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    textAlign: "center",
  },
  category: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.orange[7],
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing.xs,
  },
  title: {
    fontFamily: theme.headings?.fontFamily || '"Noto Serif TC", serif',
    fontSize: "clamp(1.5rem, 2.8vw, 2rem)",
    fontWeight: 600,
    color: theme.colors.gray[8],
    letterSpacing: "0.03em",
    lineHeight: 1.25,
  },
  summary: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.gray[6],
    lineHeight: 1.75,
    marginTop: theme.spacing.sm,
  },
  story: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    lineHeight: 1.8,
  },
  titleRule: {
    height: 2,
    width: 40,
    backgroundColor: theme.colors.orange[4],
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.lg,
  },
  breadcrumb: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.gray[5],
    marginBottom: theme.spacing.lg,
    letterSpacing: "0.02em",
  },
}));

function SpecificProductPage(props) {
  const { classes } = useStyles();
  const product = props.product;
  if (!product) return <Navigate to="/product" replace />;

  const imgList = product.image;
  const altBase = `${product.title} - 馬來西亞貓山王朝榴槤產品`;
  const imgListCard = imgList.map((img, i) => (
    <Carousel.Slide key={i}>
      <Image
        fit="contain"
        height="100%"
        width="100%"
        src={img}
        alt={imgList.length > 1 ? `${altBase} 圖片${i + 1}` : altBase}
        loading={i === 0 ? "eager" : "lazy"}
      />
    </Carousel.Slide>
  ));

  return (
    <>
      <PageMeta
        title={product.title}
        description={product.meta_description || `${product.title}｜貓山王朝｜馬來西亞頂級榴槤批發零售`}
        canonicalPath={`/${product.url_link}`}
        image={imgList[0]}
        imageAlt={product.title}
        type="product"
      />
      <ProductJsonLd product={product} />
      <BreadcrumbJsonLd
        items={[
          { name: "首頁", path: "/" },
          { name: "產品", path: "/product" },
          { name: product.title, path: `/${product.url_link}` },
        ]}
      />
      <PageContainer component="article" pt={pageTopSpacing()} pb={rem(56)}>
        <Text className={classes.breadcrumb}>首頁 / 產品 / {product.title}</Text>
        <Grid gutter={40} align="flex-start">
          <Grid.Col span={12} md={6}>
            <div className={classes.imageWrapper}>
              <Carousel withIndicators={imgList.length > 1}>{imgListCard}</Carousel>
            </div>
            {product.imageCaption && <Text className={classes.caption}>{product.imageCaption}</Text>}
          </Grid.Col>

          <Grid.Col span={12} md={6}>
            <Stack spacing="lg">
              <div>
                {product.category && (
                  <Text className={classes.category}>{getCategoryLabel(product.category)}</Text>
                )}
                <Title order={1} className={classes.title}>
                  {product.title}
                </Title>
                <div className={classes.titleRule} aria-hidden />
                {product.sold_out && (
                  <Badge color="gray" variant="filled" size="md" mb="sm">
                    已售罄
                  </Badge>
                )}
                <Text className={classes.summary}>{product.summary}</Text>
              </div>

              <ProductSpecs specs={product.specs} />
              <ProductPricing pricing={product.pricing} note={product.pricingNote} />

              {product.story && (
                <>
                  <Divider color="gray.2" />
                  <Text className={classes.story}>{product.story}</Text>
                </>
              )}

              {product.sold_out ? (
                <Button color="gray" radius="md" size="md" disabled w={{ base: "100%", sm: "auto" }}>
                  已售罄
                </Button>
              ) : (
                <NavLink to="/contact" style={{ textDecoration: "none" }}>
                  <Button color="orange" radius="md" size="md" w={{ base: "100%", sm: "auto" }}>
                    查詢訂銷
                  </Button>
                </NavLink>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </PageContainer>
    </>
  );
}

export default SpecificProductPage;
